import { Lock } from "lucide-react";

import { PageHeader } from "@/components/page-header";
import { QueryErrorCard } from "@/components/query-error-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCustomerPermissions, useMe } from "@/lib/api";

import { ColleagueList } from "./colleague-list";
import { CustomerPermissionGrid } from "./customer-permission-grid";

export function PortalPermissionsPage() {
  const { data: me } = useMe();
  const permissionsQuery = useCustomerPermissions();
  const data = permissionsQuery.data;

  // The nav hides this page without the capability, but a customer could still
  // hit the URL directly — show a friendly no-access state instead of the grid.
  if (me && !me.capabilities?.manage_account) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <PageHeader eyebrow="Permissions" title="Access managed by J&J" />
        <div className="bg-card border-border flex items-start gap-3 rounded-lg border p-5 shadow-sm">
          <span className="bg-warm-gray-04/40 text-muted flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
            <Lock className="h-4 w-4" />
          </span>
          <p className="text-muted text-sm leading-relaxed">
            You don&apos;t have permission to manage your colleagues&apos; access. Contact your
            J&amp;J representative if you need this enabled.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
      <PageHeader
        eyebrow={data?.account_name ?? "Your account"}
        title="Colleagues & permissions"
        description="Invite colleagues and tune what each person may do through MCP. The account default is set by J&J and is the ceiling for what you can grant."
      />

      {permissionsQuery.isLoading ? (
        <div className="space-y-8">
          <Skeleton className="h-40 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      ) : permissionsQuery.isError ? (
        <QueryErrorCard
          message="Couldn't load your account permissions."
          onRetry={() => void permissionsQuery.refetch()}
        />
      ) : data ? (
        <div className="space-y-8">
          <ColleagueList contacts={data.contacts} selfContactId={me?.contact_id} />
          <section className="space-y-3">
            <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
              Permissions
            </h3>
            <CustomerPermissionGrid data={data} selfContactId={me?.contact_id} />
          </section>
        </div>
      ) : null}
    </div>
  );
}
